import React, { useState, useEffect, useMemo, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { parseISO, format, isValid } from "date-fns";
import {
  CTable,
  CContainer,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CCol,
  CFormLabel,
  CFormInput,
  CSpinner,
  CAlert,
  CCard,
  CCardBody,
  CRow,
} from "@coreui/react";
import settingsStore from "../../store/SettingsStore";
import dangerousEventsStore from "../../store/DangerousEventsStore";
import {
  buildDangerousEventRow,
  DANGEROUS_EVENT_ROW_FIELDS,
} from "../../utils/dangerousEventTypes";
import EventTypeFilter from "./EventTypeFilter";
import PeriodFilters from "../UI/PeriodFilters/PeriodFilters";
import ServerSelect from "../UI/PeriodFilters/ServerSelect";
import { fmtInt } from "../../utils/chartHelpers";
import { formatYmd } from "../../utils/dateRange";

function dayStartIso(dateStr) {
  if (!dateStr) return "";
  return `${dateStr}T00:00:00.000`;
}

function dayEndExclusiveIso(dateStr) {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + 1);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}T00:00:00.000`;
}

function formatEventDateTime(dateStr) {
  if (!dateStr) return "—";
  const raw = String(dateStr).trim();
  let d = parseISO(raw);
  if (!isValid(d)) d = parseISO(raw.replace(" ", "T"));
  if (!isValid(d)) {
    const fallback = new Date(raw);
    if (isValid(fallback)) d = fallback;
  }
  if (!isValid(d)) return raw;
  return format(d, "dd.MM.yyyy HH:mm:ss");
}

const ROW_FIELDS = DANGEROUS_EVENT_ROW_FIELDS;
const COL_COUNT = ROW_FIELDS.length;

function DangerousOpsMobileList({ rows, loading }) {
  if (rows.length === 0 && !loading) {
    return <p className="text-center text-body-secondary py-4 mb-0">Нет данных за выбранный период.</p>;
  }
  return (
    <div className="dangerous-ops-mobile dash-mobile-cards">
      {rows.map((row) => (
        <article key={row.key} className="dangerous-ops-card">
          <div className="dangerous-ops-card-type">{row.typeLabel}</div>
          <div className="dangerous-ops-card-date">{row.dateLabel}</div>
          {ROW_FIELDS.filter((f) => f.key !== "dateLabel" && f.key !== "typeLabel").map((f) => (
            <div key={f.key} className="dangerous-ops-field">
              <div className="dangerous-ops-field-label">{f.label}</div>
              <div className="dangerous-ops-field-value">{row[f.key]}</div>
            </div>
          ))}
        </article>
      ))}
    </div>
  );
}

const DangerousOpsPage = observer(() => {
  const [selectedServer, setSelectedServer] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [quickRange, setQuickRange] = useState("");

  useEffect(() => {
    const init = async () => {
      const userId = localStorage.getItem("userId");
      if (userId) await settingsStore.getServers(userId);
      const today = formatYmd(new Date());
      setDateFrom(today);
      setDateTo(today);
    };
    init();
  }, []);

  useEffect(() => {
    if (!settingsStore.servers?.length) return;
    if (selectedServer === "") {
      setSelectedServer(String(settingsStore.servers[0].id));
    }
  }, [settingsStore.servers, selectedServer]);

  const loadEvents = useCallback(
    (from, to) => {
      const f = from ?? dateFrom;
      const t = to ?? dateTo;
      if (!selectedServer || !f || !t) return;
      dangerousEventsStore.fetchEvents(selectedServer, dayStartIso(f), dayEndExclusiveIso(t), {
        employeesIncludeDeleted: true,
      });
    },
    [selectedServer, dateFrom, dateTo],
  );

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const rows = useMemo(() => {
    return (dangerousEventsStore.events || []).map((ev, idx) =>
      buildDangerousEventRow(ev, idx, formatEventDateTime),
    );
  }, [dangerousEventsStore.events]);

  const summary = dangerousEventsStore.summary;

  return (
    <CContainer fluid className="px-3" style={{ flex: "1", marginTop: "120px", maxWidth: "100%" }}>
      <CCol className="mb-3">
        <h4 className="mb-0">Опасные операции</h4>
        <small className="text-body-secondary">
          События из журнала iiko по выбранным типам
        </small>
      </CCol>

      <ServerSelect
        servers={settingsStore.servers}
        value={selectedServer}
        onChange={(e) => setSelectedServer(e.target.value)}
        id="srvDanger"
        label="Сервер iiko"
      />

      <PeriodFilters
        fromDate={dateFrom}
        toDate={dateTo}
        onFromChange={setDateFrom}
        onToChange={setDateTo}
        onRefresh={loadEvents}
        maxDays={90}
        quickRange={quickRange}
        onQuickRangeChange={setQuickRange}
        disabled={dangerousEventsStore.isLoading}
      />

      <EventTypeFilter
        selected={dangerousEventsStore.selectedEventTypes}
        onChange={(types) => {
          dangerousEventsStore.setSelectedEventTypes(types);
          loadEvents();
        }}
      />

      <CFormLabel htmlFor="orderNumSearch">Номер заказа</CFormLabel>
      <div className="d-flex gap-2 mb-3 flex-wrap">
        <CFormInput
          id="orderNumSearch"
          placeholder="Поиск по номеру заказа"
          value={dangerousEventsStore.orderNumSearch}
          onChange={(e) => dangerousEventsStore.setOrderNumSearch(e.target.value)}
          style={{ maxWidth: 280 }}
        />
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => loadEvents()}
          disabled={dangerousEventsStore.isLoading}
        >
          Найти
        </button>
      </div>

      <CRow className="g-3 mb-3">
        <CCol sm={6}>
          <CCard>
            <CCardBody>
              <div className="text-body-secondary small">Событий</div>
              <div className="fs-4 fw-semibold">{summary.count}</div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={6}>
          <CCard>
            <CCardBody>
              <div className="text-body-secondary small">Сумма по событиям</div>
              <div className="fs-4 fw-semibold">{fmtInt(summary.totalSum)} ₽</div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {dangerousEventsStore.errorMessage && (
        <CAlert color="danger" className="mb-3">
          {dangerousEventsStore.errorMessage}
        </CAlert>
      )}

      <div className="d-lg-none">
        {dangerousEventsStore.isLoading && rows.length === 0 && (
          <p className="text-center text-body-secondary py-4 mb-0">
            <CSpinner size="sm" className="me-2" />
            Загрузка…
          </p>
        )}
        <DangerousOpsMobileList rows={rows} loading={dangerousEventsStore.isLoading} />
      </div>

      <div className="d-none d-lg-block table-responsive">
        <CTable hover bordered className="mb-0 dangerous-ops-table align-middle">
          <CTableHead>
            <CTableRow>
              {ROW_FIELDS.map((f) => (
                <CTableHeaderCell key={f.key} scope="col">
                  {f.label}
                </CTableHeaderCell>
              ))}
            </CTableRow>
          </CTableHead>
          <CTableBody>
            {rows.length === 0 && !dangerousEventsStore.isLoading && (
              <CTableRow>
                <CTableDataCell colSpan={COL_COUNT} className="text-center text-body-secondary">
                  Нет данных за выбранный период.
                </CTableDataCell>
              </CTableRow>
            )}
            {rows.map((row) => (
              <CTableRow key={row.key}>
                {ROW_FIELDS.map((f) => (
                  <CTableDataCell key={f.key} className="text-break">
                    {row[f.key]}
                  </CTableDataCell>
                ))}
              </CTableRow>
            ))}
          </CTableBody>
        </CTable>
      </div>
    </CContainer>
  );
});

export default DangerousOpsPage;
